import { mapRegistrationPerformance } from '@features/forms/helpers/mapRegistrationPerformance';

import {
  FormDetail,
  FormDropOff,
  FormListItem,
  FormSourceBreakdownRow,
  FormSubmission
} from '../types';
import { FORM_TEMPLATES, LOCKED_BASIC_FIELDS, MOCK_FORMS_LIST } from './index';

type FormTemplateKey = keyof typeof FORM_TEMPLATES;

const FORM_ID_TO_TEMPLATE: Record<string, FormTemplateKey> = {
  form_annual_2026: 'annual-fee',
  form_admission: 'admission-registration',
  form_workshop: 'workshop',
  form_exam: 'exam-fee'
};

const DEFAULT_FORM_TEMPLATE: FormTemplateKey = 'annual-fee';

export const MOCK_SOURCE_BREAKDOWN: FormSourceBreakdownRow[] = [
  { source: 'WhatsApp', views: 3, paid: 2, convRate: 2 },
  { source: 'Facebook', views: 6, paid: 2, convRate: 2 },
  { source: 'Instagram', views: 4, paid: 3, convRate: 2 },
  { source: 'Website', views: 9, paid: 2, convRate: 2 },
  { source: 'Direct', views: 12, paid: 6, convRate: 2 }
];

const buildFormDetail = (item: FormListItem): FormDetail => {
  const templateKey = FORM_ID_TO_TEMPLATE[item.id] ?? DEFAULT_FORM_TEMPLATE;
  const template = FORM_TEMPLATES[templateKey];

  return {
    ...item,
    viewCount: 890,
    conversionRate: 16.4,
    description: '<p>Registration form for students and parents.</p>',
    feeAmount: template.feeAmount ?? 0,
    acceptInstallments: template.acceptInstallments,
    slug: (item.name?.toLowerCase() || '').replace(/\s+/g, '-'),
    publicUrl: `https://forms.cofee.life/${encodeURIComponent(item.name)}`,
    formVersion: 2,
    fields: template.defaultFields,
    createdAt: '2026-04-24T08:00:00.000Z',
    autoCloseDate: '2026-06-26T23:59:59.000Z',
    templateId: template.id,
    sourceTrackingEnabled: item.sourceTrackingEnabled,
    sourceBreakdown:
      item.status === 'published' ? MOCK_SOURCE_BREAKDOWN : undefined,
    totalRegistrations: 100,
    totalRevenue: 10000,
    totalDiscount: 12000,
    tabCounts: {
      registrations: 5,
      dropOffs: 4,
      activity: 3
    },
    registrationPerformance: mapRegistrationPerformance({
      viewed_count: 890,
      form_submitted_count: 640,
      mobile_verified_count: 580,
      paid_count: 142
    })
  };
};

export const MOCK_FORM_DETAIL_ZERO_VIEWS: FormDetail = {
  ...buildFormDetail(MOCK_FORMS_LIST[0]),
  viewCount: 0,
  registrationPerformance: mapRegistrationPerformance({
    viewed_count: 0,
    form_submitted_count: 0,
    mobile_verified_count: 0,
    paid_count: 0
  })
};

export const MOCK_FORMS_DETAILS: Record<string, FormDetail> =
  MOCK_FORMS_LIST.reduce(
    (acc, item) => ({
      ...acc,
      [item.id]: buildFormDetail(item)
    }),
    {} as Record<string, FormDetail>
  );

export const MOCK_FORM_SUBMISSIONS: FormSubmission[] = [
  {
    id: 'sub_1',
    name: 'Anjali Nair',
    phone: '+919383437343',
    amountPaid: 1000,
    totalAmount: 1800,
    addedTo: {
      mode: 'group',
      groupId: 'grp_dance',
      groupName: 'Dance Class A'
    },
    source: 'Whatsapp',
    paidAt: '2026-04-12T10:00:00.000Z',
    paymentId: 'pay_001',
    formVersion: 1,
    paymentStatus: 'processing'
  },
  {
    id: 'sub_2',
    name: 'Rahul Verma',
    phone: '+919900112233',
    amountPaid: 6000,
    totalAmount: 6000,
    addedTo: { mode: 'member_list' },
    memberId: 'mem_sub_2',
    source: 'Instagram',
    paidAt: '2026-04-12T11:00:00.000Z',
    paymentId: 'pay_002',
    formVersion: 2,
    paymentStatus: 'paid'
  },
  {
    id: 'sub_3',
    name: 'Priya Menon',
    phone: '+919812345678',
    amountPaid: 10000,
    totalAmount: 10000,
    addedTo: { mode: 'group', groupId: 'grp_yoga', groupName: 'Yoga class A' },
    source: 'Facebook',
    paidAt: '2026-04-11T09:00:00.000Z',
    paymentId: 'pay_003',
    formVersion: 1,
    paymentStatus: 'paid'
  },
  {
    id: 'sub_4',
    name: 'Aarav Sharma',
    phone: '+919876543210',
    amountPaid: 12000,
    totalAmount: 12000,
    addedTo: {
      mode: 'group',
      groupId: 'grp_grade8',
      groupName: 'Grade 8 — Morning batch'
    },
    source: 'Direct',
    paidAt: '2026-04-10T14:30:00.000Z',
    paymentId: 'pay_004',
    formVersion: 2,
    paymentStatus: 'paid'
  }
];

export const MOCK_FORM_DROP_OFFS: FormDropOff[] = [
  {
    id: 'drop_1',
    name: 'Rahul Verma',
    phone: '+919900112233',
    status: 'mobile_verified',
    submittedAt: '2026-04-22T09:00:00.000Z',
    source: 'WhatsApp',
    totalAmount: 12000,
    paymentOrderId: 'ord_drop_1',
    reminderMessage: 'Pay your fee: https://pay.cofee.life/ord_drop_1'
  },
  {
    id: 'drop_2',
    name: 'Priya Sharma',
    phone: '+919876543210',
    status: 'mobile_verified',
    submittedAt: '2026-04-21T15:30:00.000Z',
    source: 'Instagram',
    totalAmount: 6000,
    paymentOrderId: 'ord_drop_2',
    reminderMessage: 'Pay your fee: https://pay.cofee.life/ord_drop_2'
  },
  {
    id: 'drop_3',
    name: 'Guest user',
    phone: '+919911223344',
    status: 'mobile_unverified',
    submittedAt: '2026-04-21T11:00:00.000Z',
    source: 'Direct',
    totalAmount: 12000,
    paymentOrderId: '',
    reminderMessage: ''
  },
  {
    id: 'drop_4',
    name: 'Ankit Joshi',
    phone: '+919988776655',
    status: 'mobile_unverified',
    submittedAt: '2026-04-20T08:00:00.000Z',
    source: 'Website',
    totalAmount: 1800,
    paymentOrderId: '',
    reminderMessage: ''
  }
];

export const getDefaultWizardFields = () => [...LOCKED_BASIC_FIELDS];
